/**
 * Resolves the household tax context that `disposalCgt.computeDisposalCgt` needs.
 *
 * Kept separate from `disposalCgt.ts` so THAT module stays pure (no dependency on
 * `calculateFinancials`) and can be unit-tested and called from inside a projection year without
 * recomputing the household position.
 *
 * Note: this module and `financialCalculations.ts` sit in the same pre-existing, safe import cycle as
 * `entityDisposalTax.ts` — references are function-body-only, never evaluated at module load.
 */
import { calculateFinancials } from '../../../server/src/rest/calculation/assessment/financialCalculations.ts';
import { taxContextFromAssessableIncomes, type DisposalTaxContext } from './disposalCgt.ts';
import type { FactFind } from './factFindTypes.ts';

/** A `calculateFinancials` result, of which only the tax fields matter here. */
export interface FinancialsWithTaxContext {
    taxContext?: DisposalTaxContext;
    clientAssessableIncome?: number;
    partnerAssessableIncome?: number;
    [key: string]: unknown;
}

/**
 * Prefer an already-computed `financials.taxContext` (free); otherwise derive it from `factFind`.
 * Falls back to income-only context if neither is available — see `taxContextFromAssessableIncomes`
 * for what that costs.
 */
export function resolveDisposalTaxContext(
    factFind: FactFind | null | undefined,
    financials?: FinancialsWithTaxContext | null,
): DisposalTaxContext {
    if (financials?.taxContext) return financials.taxContext;
    if (factFind) {
        const computed = calculateFinancials(factFind) as FinancialsWithTaxContext;
        if (computed?.taxContext) return computed.taxContext;
    }
    return taxContextFromAssessableIncomes(
        financials?.clientAssessableIncome || 0,
        financials?.partnerAssessableIncome || 0,
        factFind?.adviceFor === 'couple',
    );
}
